import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientContext } from '@/common/application/contexts/ClientContext';
import axios from '@/common/infrastructure/axios';
import { getUserIdFromSession } from '@/home/application/utils/sessionUtils';

interface VerifyClientAuthorizationResponse {
  IsLoggedIn: boolean;
  HasClient: boolean;
  MappingID: number | null;
}

/**
 * Component that handles post-login actions like client switching
 * Should be placed in routes that have access to ClientProvider
 */
const PostLoginHandler: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedGlobalUserMappingID, clients, clientsLoading } = useClientContext();
  
  // Track if we've already handled the post-login flow to prevent infinite loops
  const hasHandledNoClients = useRef(false);

  useEffect(() => {
    const handlePostLogin = async () => {
      // Check if there's post-login data in sessionStorage first
      const returnTo = sessionStorage.getItem('postLoginReturnTo');
      const clientID = sessionStorage.getItem('postLoginClientID');
      const cusID = sessionStorage.getItem('postLoginCusID');
      
      // If no post-login data, nothing to do
      if ( !clientID || !cusID) {
        return;
      }

      // Wait for clients to finish loading
      if (clientsLoading) {
        console.log('PostLoginHandler: Waiting for clients to load...');
        return;
      }

      // If no clients are available but we have post-login data, clear it and navigate to dashboard
      if (clients.length === 0) {
        // Only handle this once to prevent infinite loops
        if (hasHandledNoClients.current) {
          return;
        }
        hasHandledNoClients.current = true;
        
        console.log('PostLoginHandler: No clients available, clearing post-login data and navigating to dashboard');
        // Clear the stored data since we can't proceed with invoice navigation
        sessionStorage.removeItem('postLoginClientID');
        sessionStorage.removeItem('postLoginCusID');
        sessionStorage.removeItem('postLoginReturnTo');
        // Navigate to dashboard - HomePage will show pending approval message
        navigate('/', { replace: true });
        return;
      }

      if (returnTo && clientID && cusID) {
        console.log(`PostLoginHandler: Found post-login context, verifying client authorization...`);
        
        try {
          // Get current userID
          const userID = getUserIdFromSession();
          if (!userID) {
            console.error('PostLoginHandler: No userID found in session');
            return;
          }

          // Make API call to verify client authorization and get MappingID
          const response = await axios.get<VerifyClientAuthorizationResponse>(
            `identity/verify-client-authorization/clientID/${clientID}/userID/${userID}/cusID/${cusID}`
          );

          console.log(`PostLoginHandler: API response:`, response.data);

          if (response.data.IsLoggedIn && response.data.HasClient && response.data.MappingID) {
            console.log(`PostLoginHandler: Switching to client with MappingID: ${response.data.MappingID}`);
            
            // Switch to the correct client
            setSelectedGlobalUserMappingID(response.data.MappingID, true);
            
            // Clear the stored data
            sessionStorage.removeItem('postLoginClientID');
            sessionStorage.removeItem('postLoginCusID');
            
            // Navigate to the returnTo URL after a short delay
            console.log(`PostLoginHandler: Navigating to ${returnTo}`);
            setTimeout(() => {
              sessionStorage.removeItem('postLoginReturnTo');
              navigate(returnTo, { replace: true });
            }, 100);
          } else {
            console.warn('PostLoginHandler: User not authorized for this client');
            // Clear the stored data even on failure
            sessionStorage.removeItem('postLoginClientID');
            sessionStorage.removeItem('postLoginCusID');
            sessionStorage.removeItem('postLoginReturnTo');
          }
        } catch (error) {
          console.error('PostLoginHandler: Error verifying client authorization:', error);
          // Clear the stored data on error
          sessionStorage.removeItem('postLoginClientID');
          sessionStorage.removeItem('postLoginCusID');
          sessionStorage.removeItem('postLoginReturnTo');
        }
      }
    };

    handlePostLogin();
  }, [navigate, setSelectedGlobalUserMappingID, clients, clientsLoading]);

  return null; // This component doesn't render anything
};

export default PostLoginHandler;

